import { useState, useEffect } from 'react';
import { getTenantSubdomain } from '../utils/tenant';
import { getCustomDomainMapping } from '../utils/customDomain';

/**
 * Storefront access gate (v4.9) — client-side twin of the backend
 * `require_storefront_enabled` guard.
 *
 * Every storefront-rendering page (MealPassProgramStore, StorefrontCheckout,
 * MealPlansCatalogue) calls this hook. When the tenant's Settings → Portal
 * "Enable Storefront" toggle is OFF, the visitor is redirected to the
 * customer login page — the storefront must be neither visible nor
 * accessible. When it's ON, the page is cleared to render.
 *
 * v4.9.2 — the check now uses GET /api/settings/public with the SAME robust
 * tenant resolution as usePortalConfig (customer-session tenant → admin-user
 * tenant → session subdomain → hostname subdomain → ?tenant= override). The
 * old check used the subdomain-path storefront-settings endpoint, which only
 * worked when a hostname subdomain existed — so logged-in customers on the
 * root host or localhost could never reach the storefront even when ENABLED.
 *
 * FAIL-CLOSED: the page only renders when the backend positively reports
 * enabled. The `?preview=1` admin-designer iframe is the single bypass (its
 * data endpoints are still tenant-gated server-side).
 *
 * Returns { checking, allowed } — render nothing (or a spinner) while
 * `checking`; `allowed` is only true when enabled or in preview.
 */

const buildSettingsUrl = (subdomain, storeSlug) => {
  const base = process.env.REACT_APP_BACKEND_URL;
  // Tenant resolution is its OWN chain (each step only runs when the previous
  // found nothing) — the store slug is appended separately so it can never
  // short-circuit that fallback ladder.
  const params = [];
  const custom = getCustomDomainMapping();
  if (custom) {
    const query = new URLSearchParams({ subdomain: custom.subdomain });
    if (custom.store_slug || storeSlug) query.set('store_slug', custom.store_slug || storeSlug);
    return `${base}/api/settings/public?${query}`;
  }
  // (1) An EXPLICIT ?tenant=/?subdomain= query param wins over everything (founder
  // prod bug, 2026-07-25). On the aggregator domain (plateos.io/order?tenant=<vendor>)
  // a visitor signed into a DIFFERENT tenant's admin/customer session had the gate
  // checked against THAT tenant — the vendor store never resolved and ordering was
  // wrongly blocked ("not available"), while incognito (no session) worked. The
  // query param is an unambiguous statement of which tenant the page is rendering,
  // so it — and ONLY it — must beat stored login state. (The HOSTNAME does NOT
  // outrank the session: a shared/app host resolves to a non-tenant label —
  // app.plateos.io→'app', preview→'staging' — that would short-circuit a valid
  // session and block the storefront; Codex P1 + bugbot. It sits below, at (3).)
  try {
    const qp = new URLSearchParams(window.location.search);
    const tParam = qp.get('tenant') || qp.get('subdomain');
    if (tParam) params.push(`subdomain=${encodeURIComponent(tParam)}`);
  } catch { /* noop */ }
  // (2) then the ambient login — a logged-in customer/admin (the v4.9.2 case).
  // Above the hostname so a shared host (app.plateos.io / preview) never overrides
  // the tenant the customer is actually signed into.
  if (!params.length) {
    try {
      const session = JSON.parse(sessionStorage.getItem('customerSession') || '{}');
      const tenantId = session?.tenant?.id || session?.tenant_id;
      if (tenantId) params.push(`tenant_id=${encodeURIComponent(tenantId)}`);
      else {
        const sub = session?.tenant_subdomain || session?.tenant?.subdomain;
        if (sub) params.push(`subdomain=${encodeURIComponent(sub)}`);
      }
    } catch { /* noop */ }
  }
  if (!params.length) {
    try {
      const user = JSON.parse(localStorage.getItem('plateos_user') || '{}');
      if (user?.tenant_id) params.push(`tenant_id=${encodeURIComponent(user.tenant_id)}`);
    } catch { /* noop */ }
  }
  // (3) then the hostname, via the CANONICAL shared resolver (utils/tenant.js): a
  // real *.plateos.io subdomain resolves to itself, preview hosts → 'staging', and
  // main / localhost / IP / marketplace hosts → null (skipped). Below the session
  // by design (see (1)); on a real tenant-subdomain host the per-origin session and
  // the hostname agree anyway, so this only matters as a signed-out fallback. Codex
  // P2 — resolved via the shared resolver, never a hand-rolled hostname-label
  // parse, which mis-reads a preview label / IP octet as a tenant.
  if (!params.length) {
    try {
      const hostTenant = getTenantSubdomain();
      if (hostTenant) params.push(`subdomain=${encodeURIComponent(hostTenant)}`);
    } catch { /* noop */ }
  }
  // (4) LAST resort — the tenant the CALLER resolved (QROrderPage's ctx.tenant
  // from a decoded QR token, or a storefront's getSubdomain() default). This is
  // deliberately LAST: it must NEVER override a real session. Codex P1 —
  // StorefrontCheckout's own getTenantSubdomain() substitutes the STORE SLUG
  // when it has no real tenant identity; for a multi-store tenant that slug is
  // NOT the tenant subdomain, so letting it win would send subdomain=<storeSlug>,
  // fail tenant resolution, and block a checkout the session could resolve.
  if (!params.length && subdomain) {
    params.push(`subdomain=${encodeURIComponent(subdomain)}`);
  }
  // v5.12 (bugbot HIGH) — the PER-STORE storefront override decides for a
  // store-scoped page. Without the slug the backend can only answer the
  // tenant-level question, so a store that is turned OFF would still render
  // client-side while its ordering endpoints 403.
  if (storeSlug) params.push(`store_slug=${encodeURIComponent(storeSlug)}`);
  return `${base}/api/settings/public${params.length ? `?${params.join('&')}` : ''}`;
};

const useStorefrontGate = (subdomain, storeSlug, opts = {}) => {
  const _qp = new URLSearchParams(window.location.search);
  const isPreview = _qp.get('preview') === '1';
  // v5.13 (bugbot HIGH) — a ``?track=`` deep link (Stripe return / "order
  // received" email) belongs to an order that is ALREADY placed and paid. This
  // hook must not REDIRECT it away: the page-level guards in QROrderPage render
  // the public tracking screen instead, and /api/dine-in/orders/track is public
  // by design. Note this suppresses only the redirect — `allowed` stays false,
  // so a disabled storefront still renders nothing but the tracking view.
  const hasTrackToken = !!_qp.get('track');
  // Marketplace v1 (founder prod bug, 2026-07-25) — the ACCOUNTLESS /order rail
  // must NEVER bounce a customer to a tenant customer-login. A marketplace
  // hand-off lands on the AGGREGATOR domain (plateos.io/order?tenant=…), so a
  // ``/customers/<slug>/login`` redirect is a guaranteed dead end: that login
  // belongs to the store's OWN subdomain and the slug can't resolve there. The
  // caller (QROrderPage) passes noRedirect so a disabled/undeliverable gate
  // renders its honest "not available" screen in place — never a login loop.
  const noRedirect = !!opts.noRedirect;
  // Marketplace decoupling (founder, 2026-07-25) — a MARKETPLACE order (`?mkt=1`)
  // is allowed when the store is marketplace-LISTED even if its own storefront
  // ("this store sells online") is off. The two toggles are independent.
  const marketplaceOrder = !!opts.marketplaceOrder;
  const [state, setState] = useState({ checking: !isPreview, allowed: isPreview });

  useEffect(() => {
    if (isPreview) return;
    let cancelled = false;
    const check = async () => {
      let enabled = false;
      try {
        const res = await fetch(buildSettingsUrl(subdomain, storeSlug));
        if (res.ok) {
          const data = await res.json();
          enabled = data.storefront_enabled === true
            || (marketplaceOrder && data.marketplace_listed === true);
        }
      } catch (e) {
        console.debug('Storefront gate check failed:', e);
      }
      if (cancelled) return;
      if (!enabled) {
        // An already-paid order (?track=) OR the accountless /order rail
        // (noRedirect) stays put: report not-allowed (the caller renders an
        // honest state) but never bounce the customer to /login — a login
        // redirect on the aggregator domain, or for any ordering session, is
        // the wrong destination.
        if (hasTrackToken || noRedirect) {
          setState({ checking: false, allowed: false });
          return;
        }
        const dest = storeSlug ? `/customers/${storeSlug}/login` : '/customers/login';
        window.location.replace(dest);
        return;
      }
      setState({ checking: false, allowed: true });
    };
    check();
    return () => { cancelled = true; };
    // Include the URL-derived flags so a change to ?mkt= / ?track= re-evaluates
    // the gate (bugbot MED — a marketplace arrival must re-check on a param
    // change, not keep a stale permission from the first render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subdomain, storeSlug, marketplaceOrder, noRedirect, hasTrackToken]);

  return { ...state, isPreview };
};

export default useStorefrontGate;
